/**
 * TanStack Query hook for the audit log endpoint (plan 01-04 backend).
 *
 * AUD-03: the backend already scopes responses per role (HR sees only their own
 * actions; Content Admin and Super-admin see everything). The frontend renders
 * what the API returns — NO client-side scope filtering. This is defense-in-depth
 * even though the UI also hides the user_id filter for HR roles.
 */
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { AuditLogPage } from "@/types/api";

export interface AuditFilters {
  action?: string;
  entity_type?: string;
  entity_id?: number;
  user_id?: number;
  from_ts?: string;
  to_ts?: string;
  include_archived?: boolean;
  limit?: number;
  offset?: number;
}

export function useAudit(filters: AuditFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.action) qs.set("action", filters.action);
  if (filters.entity_type) qs.set("entity_type", filters.entity_type);
  if (filters.entity_id !== undefined)
    qs.set("entity_id", String(filters.entity_id));
  if (filters.user_id !== undefined) qs.set("user_id", String(filters.user_id));
  if (filters.from_ts) qs.set("from_ts", filters.from_ts);
  if (filters.to_ts) qs.set("to_ts", filters.to_ts);
  // AUD-04: append only when true so default requests are byte-identical to today.
  if (filters.include_archived) qs.set("include_archived", "true");
  qs.set("limit", String(filters.limit ?? 100));
  qs.set("offset", String(filters.offset ?? 0));

  return useQuery({
    queryKey: ["audit", qs.toString()],
    queryFn: () => apiFetch<AuditLogPage>(`/api/audit?${qs}`),
  });
}
